/**
 * Firebase configuration for the public marketing site.
 *
 * Architectural rule: this site does not write to Firestore directly. Every
 * write (contact, newsletter, future engagement events) goes through
 * a Cloud Function. App Check is the trust boundary — the Functions backend
 * (PR #24 in home-ceo-backend) enforces it per-callable via
 * `enforceAppCheck: APP_CHECK_ENFORCED` plus a `requireWebClient` provider-
 * source check that rejects mobile-attested tokens reaching public callables.
 * The convention is enforced by absence — `firebase/firestore` is intentionally
 * not imported here. If you find yourself needing a Firestore write from the
 * browser, add it to the Cloud Function instead.
 *
 * What lives here:
 * - Firebase App init (needed for Functions httpsCallable and App Check).
 * - Firebase App Check, reCAPTCHA Enterprise provider, init before any other
 *   Firebase service so callables outbound from this client carry App Check
 *   tokens. In dev (`import.meta.env.DEV`), Firebase mints a debug token that
 *   the developer registers per-device in Firebase Console — production keys
 *   skip localhost in their domain allow-list, so debug tokens are the only
 *   way local dev hits enforced callables.
 *
 * What is intentionally absent:
 * - Firestore client (writes go through Cloud Functions; no reads needed).
 * - Auth, Messaging, Performance, Crashlytics — none used on this site.
 * - **Firebase Analytics.** Removed with the logging rebuild. It was gated
 *   on a cookie-consent record that no shipped UI ever writes, so collection
 *   was off for every visitor by design, and the GA4 property's BigQuery
 *   export has been dry since 2026-08-18. Site telemetry now goes to
 *   `the-home-ceo.server_logs` via `src/logging`, which is cookieless and
 *   needs no consent gate. Reviving GA4 would mean shipping a consent
 *   banner — see the "No cookie banner" decision in docs/backlog.md.
 */

import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
/* Deep import, deliberately. `@logging` pulls in the event pipeline, whose
   HTTP transport needs the App Check instance exported below — importing
   the barrel here would close an initialization cycle. `console-logger` is
   a leaf with no pipeline imports, so this edge is safe and one-way. */
import { createLogger } from '@logging/console-logger';

const firebaseLogger = createLogger('Firebase');

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let app: FirebaseApp;
try {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
} catch (error) {
  firebaseLogger.error('Initialization error:', error);
  throw error;
}

/* App Check — initialize before any service that calls a backend so the
   token is attached. The reCAPTCHA Enterprise site key (public) is shared
   between web and the App Check provider; production keys skip localhost
   in the domain allow-list, so local dev uses a per-machine debug token
   set via VITE_APPCHECK_DEBUG_TOKEN (UUID generated by the backend's
   scripts/generate-app-check-debug-token.sh and registered in Firebase
   Console > App Check > Manage debug tokens).

   The instance is retained (it used to be discarded) because the logging
   transport needs `getToken()` to attach an App Check token to its ingest
   POSTs. `navigator.sendBeacon` cannot set headers, so the token has to be
   fetched ahead of time and cached rather than read off an outgoing
   callable — see src/logging/transports/http-transport.ts. Null whenever
   App Check did not initialize, which is the normal state locally without
   a debug token. */
let appCheck: AppCheck | null = null;

if (typeof window !== 'undefined' && import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  if (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string })
      .FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  }
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(
        import.meta.env.VITE_RECAPTCHA_SITE_KEY,
      ),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    firebaseLogger.error('App Check initialization failed:', error);
  }
}

export { app, appCheck };
